<script setup lang="ts">
type CustomTestData = App.Configuration.Data.CustomTestData;
type EnumData = App.Base.Data.EnumData;
type TestData = App.Students.Data.TestData;

import type { Selectable } from '@/components/selectBox/selectBox';
import type { HybridlyFormData } from '@/@types/global';

import { buildSelectableCustomTest } from '@/utils/buildSelectable';

import Divider from '@/components/divider/Divider.vue';
import Fieldset from '@/components/fieldset/Fieldset.vue';
import FormCheckboxInput from '@/components/formCheckboxInput/FormCheckboxInput.vue';
import FormDateInput from '@/components/formDateInput/FormDateInput.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormSwitchInput from '@/components/formSwitchInput/FormSwitchInput.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

const props = defineProps<{
  form: HybridlyFormData<{
    type: EnumData;
    subject: EnumData;
    test: EnumData;
    date: string;
    scores: Record<string, number>;
    is_waiver_assisted: boolean;
    is_registered: boolean;
    is_waived: boolean;
  }>;
  types: EnumData[];
  scoreMap: Record<string, Record<string, { rules?: string[]; step?: number }>>;
  labelMap: Record<string, string>;
  customTests: CustomTestData[];
  apCourses: EnumData[];
  test?: TestData;
  isReadonly?: boolean;
}>();

const currentCustomTest = computed(
  () => props.customTests.filter((test) => test.name === props.form.fields.test?.value)[0]
);

const shouldShowScores = computed(() => {
  const isComplete = props.form.fields.is_complete;
  const isNotCustomTest = props.form.fields.type && props.form.fields.type?.value !== 'custom-test';
  const isCustomTestWithValue =
    props.form.fields.type?.value === 'custom-test' && props.form.fields.test?.value;

  return isComplete && (isNotCustomTest || isCustomTestWithValue);
});

const isScoresSwitchDisabled = computed(() => {
  const hasNoTypeAndNotCustomTest =
    !props.form.fields.type && props.form.fields.type?.value !== 'custom-test';
  const isCustomTestWithoutValue =
    props.form.fields.type?.value === 'custom-test' && !props.form.fields.test?.value;
  const isApCourseWithoutSubject =
    props.form.fields.type?.value === 'ap-course' && !props.form.fields.subject?.value;

  return hasNoTypeAndNotCustomTest || isCustomTestWithoutValue || isApCourseWithoutSubject;
});

const customTestOptions = props.customTests.map((test) =>
  buildSelectableCustomTest(test)
) as Selectable<string>[];

function handleResetScores() {
  props.form.fields.scores = {};
}

function handleResetCustomTestScores() {
  ['score', 'score2', 'score3', 'score4', 'score5'].forEach((score) => {
    props.form.fields[score] = null;
  });
}

function handleTestTypeChange(type) {
  if (props.form.initial.type?.value === type.value) {
    props.form.reset();
  } else {
    props.form.fields.type = type;
    props.form.fields.test = null;
    props.form.fields.is_complete = false;
    props.form.errors = {};
    handleResetScores();
    handleResetCustomTestScores();
  }
}

function handleSubjectChange(subject) {
  props.form.fields.scores.subject = subject.value;
  props.form.fields.subject = subject;
}

function handleIsCompleteChange(isComplete) {
  if (props.form.initial.is_complete?.value === isComplete) {
    props.form.reset();
  } else {
    props.form.fields.is_complete = isComplete;
    handleResetScores();
    handleResetCustomTestScores();
    if (props.form.fields.subject?.value) {
      props.form.fields.scores.subject = props.form.fields.subject.value;
    }
  }
}
</script>

<template>
  <Fieldset title="Global information">
    <FormListboxInput
      label="Exam type"
      :error="form.errors.type"
      :isDisabled="isReadonly"
      :modelValue="form.fields.type"
      :options="types"
      :previousValue="
        !isReadonly
          ? previousValueLabel(
              form.fields.type?.label,
              types.find((t) => t.value === props.test?.type?.value)?.label
            )
          : undefined
      "
      @update:modelValue="(value) => handleTestTypeChange(value)"
    />
    <FormListboxInput
      v-if="form.fields.type?.value === 'ap-course'"
      label="Course"
      :error="form.errors.scores?.subject"
      :isDisabled="isReadonly"
      :modelValue="form.fields.subject"
      :options="apCourses"
      :previousValue="
        !isReadonly
          ? previousValueLabel(
              form.fields.subject?.label,
              props.test?.scores?.find((s) => s.name === 'subject')?.score || null
            )
          : undefined
      "
      @update:modelValue="(value) => handleSubjectChange(value)"
    />
    <FormListboxInput
      v-if="form.fields.type?.value === 'custom-test'"
      label="Test"
      :error="form.errors.test"
      :isDisabled="isReadonly"
      :modelValue="form.fields.test"
      :options="customTestOptions"
      :previousValue="
        !isReadonly ? previousValueLabel(form.fields.test?.label, props.test?.type) : undefined
      "
      @update:modelValue="(value) => (form.fields.test = value)"
    />
    <FormDateInput
      :clearable="false"
      :error="form.errors.date"
      :isDisabled="isReadonly"
      :modelValue="form.fields.date"
      :previousValue="
        !isReadonly ? previousValueLabel(form.fields.date, props.test?.date) : undefined
      "
      format="MMM dd, yyyy"
      label="Date"
      name="date"
      @update:modelValue="(value) => (form.fields.date = value)"
    />
  </Fieldset>
  <Divider />
  <Fieldset title="Scores">
    <FormSwitchInput
      :checked="form.fields.is_complete"
      :error="form.errors.is_complete"
      :isDisabled="isScoresSwitchDisabled || isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.is_complete, props.test?.is_complete)
          : undefined
      "
      label="Add Exam Scores?"
      name="is_complete"
      helperText="If the exam is complete, you can add the scores."
      @update:checked="(value) => handleIsCompleteChange(value)"
    />
    <template v-if="shouldShowScores">
      <FormRows class="lg:w-1/2">
        <template v-if="form.fields.type.value !== 'custom-test'">
          <FormTextInput
            v-for="(values, score) in scoreMap[form.fields.type?.value]"
            :key="score"
            :error="form.errors.scores?.[score]"
            :isDisabled="isReadonly"
            :label="labelMap[score]"
            :modelValue="form.fields.scores[score]"
            :previousValue="
              !isReadonly
                ? previousValueLabel(
                    form.fields.scores[score],
                    (props.test?.scores || []).find((s) => s.name === score)?.score
                  )
                : undefined
            "
            :step="values?.step"
            :name="score"
            type="number"
            @update:modelValue="(value) => (form.fields.scores[score] = value)"
          />
        </template>
        <template v-if="form.fields.type?.value === 'custom-test'">
          <FormTextInput
            v-if="currentCustomTest?.score"
            :error="form.errors?.score"
            :isDisabled="isReadonly"
            :label="currentCustomTest.score"
            :modelValue="form.fields.score"
            :previousValue="
              !isReadonly
                ? previousValueLabel(
                    form.fields.score,
                    (props.test?.scores || []).find((s) => s.name === currentCustomTest?.score)
                      ?.score
                  )
                : undefined
            "
            name="score"
            type="number"
            @update:modelValue="(value) => (form.fields.score = value)"
          />
          <FormTextInput
            v-if="currentCustomTest?.score2"
            :error="form.errors?.score2"
            :isDisabled="isReadonly"
            :label="currentCustomTest.score2"
            :modelValue="form.fields.score2"
            :previousValue="
              !isReadonly
                ? previousValueLabel(
                    form.fields.score2,
                    (props.test?.scores || []).find((s) => s.name === currentCustomTest?.score2)
                      ?.score
                  )
                : undefined
            "
            name="score2"
            type="number"
            @update:modelValue="(value) => (form.fields.score2 = value)"
          />
          <FormTextInput
            v-if="currentCustomTest?.score3"
            :error="form.errors?.score3"
            :isDisabled="isReadonly"
            :label="currentCustomTest.score3"
            :modelValue="form.fields.score3"
            :previousValue="
              !isReadonly
                ? previousValueLabel(
                    form.fields.score3,
                    (props.test?.scores || []).find((s) => s.name === currentCustomTest?.score3)
                      ?.score
                  )
                : undefined
            "
            name="score3"
            type="number"
            @update:modelValue="(value) => (form.fields.score3 = value)"
          />
          <FormTextInput
            v-if="currentCustomTest?.score4"
            :error="form.errors?.score4"
            :isDisabled="isReadonly"
            :label="currentCustomTest.score4"
            :modelValue="form.fields.score4"
            :previousValue="
              !isReadonly
                ? previousValueLabel(
                    form.fields.score4,
                    (props.test?.scores || []).find((s) => s.name === currentCustomTest?.score4)
                      ?.score
                  )
                : undefined
            "
            name="score4"
            type="number"
            @update:modelValue="(value) => (form.fields.score4 = value)"
          />
          <FormTextInput
            v-if="currentCustomTest?.score5"
            :error="form.errors?.score5"
            :isDisabled="isReadonly"
            :label="currentCustomTest.score5"
            :modelValue="form.fields.score5"
            :previousValue="
              !isReadonly
                ? previousValueLabel(
                    form.fields.score5,
                    (props.test?.scores || []).find((s) => s.name === currentCustomTest?.score5)
                      ?.score
                  )
                : undefined
            "
            name="score5"
            type="number"
            @update:modelValue="(value) => (form.fields.score5 = value)"
          />
        </template>
      </FormRows>
    </template>
  </Fieldset>
  <Divider />
  <Fieldset title="Other information">
    <FormCheckboxInput
      :checked="form.fields.is_registered"
      :error="form.errors.is_registered"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.is_registered, props.test?.is_registered)
          : undefined
      "
      label="Has registered"
      name="is_registered"
      helperText="Has the student registered to take the exam?"
      @update:checked="(value) => (form.fields.is_registered = value)"
    />
    <FormCheckboxInput
      :checked="form.fields.is_waived"
      :error="form.errors.is_waived"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly ? previousValueLabel(form.fields.is_waived, props.test?.is_waived) : undefined
      "
      label="Has waiver"
      name="is_waived"
      helperText="Has the student received a waiver?"
      @update:checked="(value) => (form.fields.is_waived = value)"
    />
    <FormCheckboxInput
      :checked="form.fields.is_waiver_assisted"
      :error="form.errors.is_waiver_assisted"
      :isDisabled="isReadonly"
      :previousValue="
        !isReadonly
          ? previousValueLabel(form.fields.is_waiver_assisted, props.test?.is_waiver_assisted)
          : undefined
      "
      label="Helped with waiver"
      name="is_waiver_assisted"
      helperText="Did you help the student receive a waiver?"
      @update:checked="(value) => (form.fields.is_waiver_assisted = value)"
    />
  </Fieldset>
</template>
